import { Grid, InputBase, inputBaseClasses, styled } from '@mui/material';
import { Close as CloseIcon, Search as SearchIcon } from '@mui/icons-material';
import classnames from 'classnames';
import { CONNECTORS_RESOURCE_TYPES } from 'js/constants/resourceTypes';
import { ConnectionLoader } from 'new/elements/ConnectionLoader/ConnectionLoader';
import { CredentialFilterToggle } from 'new/elements/flows/SelectConnection/CredentialFilterToggle';
import { getCredentialCount } from 'js/reducers/credentials';
import { EmptyState } from 'new/elements/EmptyState';
import {
  SUPPORT_KEY_SINK_FORM_SELECT,
  SUPPORT_KEY_SOURCE_FORM_SELECT,
} from 'js/constants/supportPageKeys';
import { filter, groupBy, has, includes, isEqual, isNil, keys, map, sortBy, toLower } from 'lodash';
import RoutePrefixContext from 'new/contexts/RoutePrefixContext';
import React from 'react';
import { useFeatureFlag } from 'new/hooks/useFeatureFlags';

import './SelectConnection.scss';

const displayNoneStyle = { display: 'none' };
const cursorPointerStyle = { cursor: 'pointer' };
const styles = {
  tooltipButton: {
    backgroundColor: '#1f3449',
    width: '14px',
    color: 'white',
    height: '14px',
    fontSize: '8.5px',
    padding: 0,
    border: 'none',
    borderRadius: '18px',
    marginLeft: '12px',
  },
  listRoot: {
    marginTop: 40,
    marginLeft: 20,
    marginRight: 20,
  },
  searchIcon: {
    color: '#BAC8CC',
    fontSize: 12,
  },
  closeIcon: {
    color: '#BAC8CC',
    cursor: 'pointer',
    fontSize: 12,
  },
  textField: {
    hint: {
      color: '#BAC8CC',
      fontWeight: 'normal',
    },
  },
  searchTextField: {
    root: {
      padding: '5px',
      width: '100%',
      fontSize: 12,
    },
    input: {
      // textAlign: 'right'
    },
    inputHint: {
      fontSize: 12,
      color: '#ADBDCC',
      lineHeight: '24px',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      // width: '85%',
      height: 'auto',
    },
  },
};

const muiStyles = {
  inputBase: {
    root: {
      fontSize: 12,
      lineHeight: '24px',
    },
    input: {
      padding: 5,
    },
  },
};

const StyledInputBase = styled(InputBase)({
  ...muiStyles.inputBase.root,
  [`.${inputBaseClasses.input}`]: muiStyles.inputBase.input,
});

const _getConnectionGroups = (props) => {
  const { connectionTypes, connectionsAllowed, connectionMetas, connectionCategories } = props;

  // Filter connection types based on connectionCategories if provided
  let filteredConnectionTypes = connectionTypes;
  let filteredConnectionsAllowed = connectionsAllowed;

  if (connectionCategories?.length > 0) {
    filteredConnectionTypes = filter(connectionTypes, (type) => {
      const meta = connectionMetas[type];
      return meta && includes(connectionCategories, meta.connectionCategory);
    });

    // Also filter connectionsAllowed to only include the filtered types
    filteredConnectionsAllowed = filter(connectionsAllowed, (type) => {
      const meta = connectionMetas[type];
      return meta && includes(connectionCategories, meta.connectionCategory);
    });
  }

  const groupedConnections = groupBy(filteredConnectionTypes, (type) => {
    if (filteredConnectionsAllowed.indexOf(type) === -1) {
      return 'Coming Soon';
    }
    return connectionMetas[type].industryCategory;
  });
  const industryCategories = sortBy(keys(groupedConnections));

  return {
    connectionsByCategory: {
      All: filteredConnectionsAllowed,
      ...groupedConnections,
    },
    allCategories: ['All', ...industryCategories],
  };
};

class SelectConnection extends React.Component {
  static contextType = RoutePrefixContext;

  constructor(props) {
    super(props);
    this.state = { ...this._getConnectionsState(props), showOnlyWithCredentials: false };

    this._getConnectionsState = this._getConnectionsState.bind(this);
    this._renderCategoryBasedView = this._renderCategoryBasedView.bind(this);
    this._renderSingleCategoryView = this._renderSingleCategoryView.bind(this);

    this._renderOption = this._renderOption.bind(this);
    this._handleConnectionSelected = this._handleConnectionSelected.bind(this);
    this._changeSelectedCategory = this._changeSelectedCategory.bind(this);
    this._onSearchTextChanged = this._onSearchTextChanged.bind(this);
    this._onSearchTextCleared = this._onSearchTextCleared.bind(this);
    this._renderSearchView = this._renderSearchView.bind(this);
    this._renderConnectionItems = this._renderConnectionItems.bind(this);

    this._handleToggleCredentialFilter = this._handleToggleCredentialFilter.bind(this);
    this._getFilteredItems = this._getFilteredItems.bind(this);
  }

  componentDidMount() {
    if (
      this.props.connectorResourceType === CONNECTORS_RESOURCE_TYPES.DESTINATION ||
      this.props.connectorResourceType === CONNECTORS_RESOURCE_TYPES.QUARANTINE
    ) {
      this.props.setContextualSupport(SUPPORT_KEY_SINK_FORM_SELECT);
    } else {
      this.props.setContextualSupport(SUPPORT_KEY_SOURCE_FORM_SELECT);
    }
  }

  componentWillUnmount() {
    if (
      this.props.connectorResourceType === CONNECTORS_RESOURCE_TYPES.DESTINATION ||
      this.props.connectorResourceType === CONNECTORS_RESOURCE_TYPES.QUARANTINE
    ) {
      this.props.unsetContextualSupport(SUPPORT_KEY_SINK_FORM_SELECT);
    } else {
      this.props.unsetContextualSupport(SUPPORT_KEY_SOURCE_FORM_SELECT);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const shouldRecomputeConnections =
      !isEqual(this.props.connectionTypes, prevProps.connectionTypes) ||
      !isEqual(this.props.connectionsAllowed, prevProps.connectionsAllowed) ||
      !isEqual(this.props.connectionCategories, prevProps.connectionCategories) ||
      !isEqual(this.props.connectionMetas, prevProps.connectionMetas) ||
      !isEqual(this.props.credentialsByType, prevProps.credentialsByType) ||
      this.props.isCredentialFilterEnabled !== prevProps.isCredentialFilterEnabled;

    if (shouldRecomputeConnections) {
      //TODO:R16: render directly from props + memo instead of saving in state;
      const newState = this._getConnectionsState(this.props, this.state);
      this.setState({
        ...newState,
        ...(!this.props.isCredentialFilterEnabled ? { showOnlyWithCredentials: false } : {}),
      });
    }
  }

  _getConnectionsState(fromProps, fromState = null) {
    const { allCategories, connectionsByCategory } = _getConnectionGroups(fromProps);

    let selectedCategory = 'All',
      filteredItems = connectionsByCategory.All,
      searchText = '';
    if (fromState) {
      selectedCategory = fromState.selectedCategory;
      filteredItems = this._getFilteredItems(
        fromState.searchText,
        fromState.selectedCategory,
        connectionsByCategory,
        fromState.showOnlyWithCredentials ?? false,
      );
      searchText = fromState.searchText;
    }

    return {
      connectionsByCategory,
      allCategories,
      selectedCategory,
      filteredItems,
      searchText,
    };
  }

  _changeSelectedCategory(newCategory = 'All') {
    const filteredItems = this._getFilteredItems(
      this.state.searchText,
      newCategory,
      this.state.connectionsByCategory,
      this.state.showOnlyWithCredentials,
    );
    this.setState({
      selectedCategory: newCategory,
      filteredItems,
    });
  }
  render() {
    const { connectorResourceType, isLoadingVendors, singleCategoryView } = this.props;

    const content =
      singleCategoryView || connectorResourceType === CONNECTORS_RESOURCE_TYPES.QUARANTINE
        ? this._renderSingleCategoryView()
        : this._renderCategoryBasedView();

    // Show loading overlay if vendors are being fetched
    if (isLoadingVendors) {
      return (
        <div style={{ position: 'relative', width: '100%', height: '100%' }}>
          {content}
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 9999,
              backdropFilter: 'blur(3px)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <ConnectionLoader loading message="Loading connectors..." />
          </div>
        </div>
      );
    }

    return content;
  }

  _renderSingleCategoryView() {
    const {
      connectorResourceType,
      selectedConnection,
      connectionMetas,
      headerTitle,
      headerSubtitle,
    } = this.props;
    const filteredConnections = this._renderConnectionItems();

    const selectedConnectionDisplayView =
      selectedConnection && has(connectionMetas, selectedConnection) ? (
        <div className="selected-connection-text">
          You have selected{' '}
          <span className="connection-type">{connectionMetas[selectedConnection].displayName}</span>
        </div>
      ) : (
        <div className="selected-connection-text">Please choose a type to proceed</div>
      );

    let headerView;
    if (headerTitle != null || headerSubtitle != null) {
      headerView = (
        <header>
          <h1>{headerTitle ?? 'Select Type'}</h1>
          {headerSubtitle != null && <p>{headerSubtitle}</p>}
        </header>
      );
    } else if (connectorResourceType === CONNECTORS_RESOURCE_TYPES.QUARANTINE) {
      headerView = (
        <header>
          <h1>Select Export Location Type</h1>
          <p>Please choose the type of file system you wish to use for error data export.</p>
        </header>
      );
    } else {
      headerView = (
        <header>
          <h1>Select Resource Type</h1>
          <p>Please choose the type of connection you want to configure.</p>
        </header>
      );
    }

    return (
      <section
        className={`
      select-connection ${connectorResourceType}`}
      >
        {headerView}
        <div className="connection-types-container">
          <Grid container className="connection-info-root-row">
            <Grid item className="connection-info-root-col" xs={12}>
              {this.props.isCredentialFilterEnabled && (
                <div className="credential-filter-container">
                  <CredentialFilterToggle
                    checked={this.state.showOnlyWithCredentials}
                    onChange={this._handleToggleCredentialFilter}
                  />
                </div>
              )}
              <div className="selected-connections-container">
                <div className="selected-connection-view">{selectedConnectionDisplayView}</div>
              </div>
            </Grid>
          </Grid>
          <Grid container className="connection-content-grid">
            <Grid item className="connection-content-root" xs={12}>
              <div className="connection-items-container">{filteredConnections}</div>
            </Grid>
          </Grid>
        </div>
      </section>
    );
  }

  _renderCategoryBasedView() {
    const { allCategories, selectedCategory, searchText } = this.state;
    const { connectionMetas, selectedConnection, connectorResourceType } = this.props;

    const categorySearchView = this._renderSearchView();
    const isInSearchMode = searchText && searchText !== '' ? true : false;
    const categoryListing = map(allCategories, (c) => {
      const className =
        isInSearchMode === false && c === selectedCategory
          ? 'category-item selected'
          : 'category-item';
      return (
        <div
          className={className}
          key={c}
          onClick={(e) => {
            e.stopPropagation();
            this._changeSelectedCategory(c);
          }}
          data-testid={`category-${c}`}
        >
          {c}
        </div>
      );
    });

    const filteredConnections = this._renderConnectionItems();

    const selectedConnectionDisplayView =
      selectedConnection && has(connectionMetas, selectedConnection) ? (
        <div className="selected-connection-text">
          You have selected{' '}
          <span className="connection-type">{connectionMetas[selectedConnection].displayName}</span>
        </div>
      ) : (
        <div className="selected-connection-text">Please choose a type to proceed</div>
      );

    return (
      <section className={`select-connection ${connectorResourceType}`}>
        <div className="connection-types-container">
          <Grid container className="connection-info-root-row">
            <Grid
              item
              className={classnames('connection-info-root-col', {
                'connection-info-root-col--centered-search': !this.props.isCredentialFilterEnabled,
              })}
              xs={12}
            >
              <div className="search-bar-container">{categorySearchView}</div>
              {this.props.isCredentialFilterEnabled && (
                <div className="credential-filter-container">
                  <CredentialFilterToggle
                    checked={this.state.showOnlyWithCredentials}
                    onChange={this._handleToggleCredentialFilter}
                  />
                </div>
              )}
            </Grid>
          </Grid>
          <Grid container className="connection-categories-div">
            <Grid item xs={4} md={3} lg={2} className="connection-categories-root">
              <div className="category-list-container">
                <div className="category-list-items">
                  <div key={'category-title'} className="category-item header">
                    <div className="title-row">CATEGORIES</div>
                  </div>
                  {categoryListing}
                </div>
              </div>
            </Grid>
            <Grid item className="connection-content-root" xs={8} md={9} lg={10}>
              <div className="selected-connections-container">
                <div className="selected-connection-view">{selectedConnectionDisplayView}</div>
              </div>
              <div className="connection-items-container">{filteredConnections}</div>
            </Grid>
          </Grid>
        </div>
      </section>
    );
  }

  _renderConnectionItems() {
    const { filteredItems } = this.state;

    if (!this.props.isLoadingVendors && filteredItems.length === 0) {
      return <EmptyState title="No connectors match your filters." />;
    }

    const connectionOptionViews = map(filteredItems, this._renderOption);
    return <div className="connection-items">{connectionOptionViews}</div>;
  }

  _renderOption(type, index) {
    const { connectionsAllowed, connectionMetas, connectionsComingSoon, credentialsByType } =
      this.props;

    //if connection is in coming soon then it is not allowed for now.
    const isAllowed = includes(connectionsAllowed, type) && !includes(connectionsComingSoon, type);
    const isChecked = this.props.selectedConnection === type;
    const isOtherChecked = !isChecked && this.props.selectedConnection;

    // I'm removing this line cause it's not useful right now. We can check a better way to do it later
    // let isPermitted = true; //TODO: Was checking for isAllowed, but do we even need that now

    const meta = connectionMetas[type];
    const credentialCount = getCredentialCount(credentialsByType, type);

    return isAllowed ? (
      <label key={index}>
        <input
          name="radio-option"
          type="radio"
          value={type}
          checked={isChecked}
          style={displayNoneStyle}
          disabled={false}
          onChange={this._handleConnectionSelected(type, index)}
        />
        <div
          data-testid={`connection-card-${meta.name}`}
          className={classnames(
            {
              'nex-form-card': true,
              'nex-form-card-selected': isChecked,
              'nex-form-card-faded': isOtherChecked,
            },
            `add-source-form`,
            `connection-card-${type}`,
          )}
          style={cursorPointerStyle}
        >
          <div
            className="img-placeholder"
            style={meta.icon ? { backgroundImage: `url(${meta.icon})` } : {}}
          />
          <h3>{meta.name}</h3>
          {credentialCount > 0 && (
            <div
              className="credential-count-badge"
              role="img"
              aria-label={`${credentialCount} ${credentialCount === 1 ? 'credential' : 'credentials'}`}
              title={`${credentialCount} ${credentialCount === 1 ? 'credential' : 'credentials'}`}
            >
              <span>{credentialCount}</span>
            </div>
          )}
        </div>
      </label>
    ) : (
      <label key={index}>
        <div
          data-testid={`connection-card-${meta.name}`}
          className={classnames(
            {
              'nex-form-card': true,
              'nex-form-card-soon': true,
            },
            `add-source-form`,
            `connection-card-${type}`,
          )}
        >
          <div
            className="img-placeholder"
            style={meta.icon ? { backgroundImage: `url(${meta.icon})` } : {}}
          />
          <h3>{meta.name}</h3>
          <div className={'coming-soon-badge'}>
            <span>Coming Soon</span>
          </div>
        </div>
      </label>
    );
  }

  _handleConnectionSelected(selectedConnection, index) {
    const handle = () => {
      this.props.onSelected(selectedConnection);
      // setting timeout here just so this action happens after redux call
      setTimeout(() => {
        if (this.props.nextFormStage) {
          this.props.nextFormStage();
        }
      }, 1);
    };
    return handle;
  }

  _renderSearchView() {
    const { searchText } = this.state;
    const inputText = searchText && searchText !== '' ? searchText : '';
    return (
      <div className="search-row">
        <div className="search-container">
          <SearchIcon fontSize="large" style={styles.searchIcon} />
          <StyledInputBase
            type="text"
            placeholder="Search connectors…"
            fullWidth
            margin="dense"
            inputProps={{
              autoComplete: 'new-password',
              'aria-label': 'Search connectors',
            }}
            onChange={this._onSearchTextChanged}
            value={inputText}
          />
          {inputText !== '' && (
            <CloseIcon
              fontSize="large"
              style={styles.closeIcon}
              onClick={this._onSearchTextCleared}
            />
          )}
        </div>
      </div>
    );
  }

  _onSearchTextChanged(e) {
    const searchText = e.target.value;

    const filteredItems = this._getFilteredItems(
      searchText,
      this.state.selectedCategory,
      this.state.connectionsByCategory,
      this.state.showOnlyWithCredentials,
    );

    this.setState({
      filteredItems,
      searchText: !searchText ? '' : searchText,
    });
  }

  _onSearchTextCleared(e) {
    e.preventDefault();
    let searchText = '';
    const filteredItems = this._getFilteredItems(
      searchText,
      this.state.selectedCategory,
      this.state.connectionsByCategory,
      this.state.showOnlyWithCredentials,
    );

    this.setState({
      filteredItems,
      searchText,
    });
  }

  _handleToggleCredentialFilter(checked) {
    const filteredItems = this._getFilteredItems(
      this.state.searchText,
      this.state.selectedCategory,
      this.state.connectionsByCategory,
      checked,
    );
    this.setState({ showOnlyWithCredentials: checked, filteredItems });
  }

  _getFilteredItems(
    searchText = '',
    selectedCategory = 'All',
    connectionsByCategory = {},
    showOnlyWithCredentials = false,
  ) {
    const { connectionMetas, credentialsByType } = this.props;
    let baseItems;

    if (!searchText) {
      baseItems = has(connectionsByCategory, selectedCategory)
        ? connectionsByCategory[selectedCategory]
        : [];
    } else {
      const lowerCaseSearchText = toLower(searchText);
      baseItems = filter(connectionsByCategory.All, (item) => {
        const meta = connectionMetas[item];
        if (isNil(meta) || !has(meta, 'displayName')) return false;
        return includes(toLower(meta.displayName), lowerCaseSearchText);
      });
    }

    return this.props.isCredentialFilterEnabled && showOnlyWithCredentials && credentialsByType
      ? filter(baseItems, (item) => getCredentialCount(credentialsByType, item) > 0)
      : baseItems;
  }
}

SelectConnection.defaultProps = {
  connectorResourceType: CONNECTORS_RESOURCE_TYPES.SOURCE,
  connectionsComingSoon: [],
  connectionCategories: [],
  isLoadingVendors: false,
  singleCategoryView: false,
  headerTitle: null,
  headerSubtitle: null,
};

export default function FlaggedSelectConnection(props) {
  const isCredentialFilterEnabled = useFeatureFlag('UI_TEMP_ENABLE_CRED_FILTER');
  return <SelectConnection {...props} isCredentialFilterEnabled={isCredentialFilterEnabled} />;
}
